import {
  Component,
  h,
  Prop,
  Event,
  type EventEmitter,
  Element,
  State,
  Watch,
} from '@stencil/core';
import { OffsitesWidgetV4EventsLayer } from '../offsites-widget-v4/widget-events/offsites-widget-v4-events-layer';
import type { AnalyticsGenericEvent } from '../../../../utils/analytics/AnalyticsGenericEvent';
import type {
  IWidgetData,
  IWidgetRequestData,
} from '../offsites-widget-v4/types/offsites-widget-v4.types';
import { isMobile } from '../../../../utils/media';
import { fetchOffsiteBannersManagerData } from './offsites-widget-v3-actions';
import { FullWidthWidget } from '../functional-components/offsites-widget-components';
import { OffsitesDummyBanner } from './offsite-dummy-banner/offsite-dummy-banner';
import { OffsiteDummyBannerSkeleton } from './offsite-dummy-banner/offsite-dummy-banner-skeleton';
import { makeLogger } from '../../../../logger/logger';
import {OffsitesDummyEventsLayer} from './offsite-dummy-banner/offsites-dummy-events-layer';
import type {labelProviderInterface} from '../../../LabelProviderInterface';

const { info } = makeLogger({pathToCurrentFile: 'components/widgets/offsites-widget/offsites-widget-v3/offsites-widget-v3.tsx'});

@Component({
  tag: 'uiaas-offsites-widget-v3',
  styleUrl: 'offsites-widget-v3.scss',
  assetsDirs: ['assets/offsites-assets-v4'],
  shadow: true,
})
export class UiaasOffsitesWidgetV3 {
  get ajaxIsLoading(): boolean {
      return this._ajaxIsLoading;
  }

  set ajaxIsLoading(value: boolean) {
      this._ajaxIsLoading = value;
  }
  @Prop() readonly isDashboard: boolean = false;
  @Prop() readonly computedPlacement: boolean = false;
  @Prop() readonly isRtl: boolean = false;
  @Prop() readonly analyticsTabName?: string;
  @Prop() readonly brand: string = '';
  @Prop() readonly country: string = '';
  @Prop() readonly language: string = '';
  @Prop({ attribute: 'offsite-banners-manager-request-data' })
  readonly offsiteBannersManagerRequestData;
  @Prop({ mutable: true }) tabName: string = '';
  @Prop({ mutable: true, reflect: true }) term: string = '';
  @Prop({ mutable: true }) labelProvider: labelProviderInterface;
  @State() mobileView: boolean = isMobile();
  @State() offsiteBannersManagerData = null;
  @Element() el: HTMLElement;
  @State() bannerData: IWidgetData | {};
  @State() private _ajaxIsLoading = false;
  private bannerTimeout: NodeJS.Timeout;

  private getOffsiteBannersManagerRequestData() {
      if (typeof this.offsiteBannersManagerRequestData === 'string') {
        return JSON.parse(
          this.offsiteBannersManagerRequestData,
        ) as IWidgetRequestData;
      } else {
        return this.offsiteBannersManagerRequestData as IWidgetRequestData;
      }
  }
  private searchStartedEv: void;

  componentWillLoad() {
      this.bannerData = {};
      const that = this;
      (async () => {
          this.bannerData = await that.getOffsiteBannersManagerData(
            this.getOffsiteBannersManagerRequestData(),
          );
      })();

      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            info(
              'LabelProvider not provided in ReturnCard component',
              [name, fallback]
            );
            return fallback;
        };
      }
  }

  connectedCallback() {
      this.searchStartedEv = this.el
        .closest('uiaas-search-tab')
        ?.addEventListener('search:started', (e) => {
            this.term = e.detail.term;
        });
  }

  disconnectedCallback() {
      this.searchStartedEv = this.el
        .closest('uiaas-search-tab')
        ?.removeEventListener('search:started', (e) => {
            this.term = e.detail.term;
        });
      if(this.bannerTimeout) {
        clearTimeout(this.bannerTimeout);
      }
  }

  @Watch('term')
  async handleTermChange(newTerm, oldTerm) {
      if (newTerm !== oldTerm) {
        const requestData = this.getOffsiteBannersManagerRequestData();
        requestData.term = newTerm;
        this.bannerData = await this.getOffsiteBannersManagerData(requestData);
      }
  }

  @Event({
    eventName: 'search:widgetEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    statisticsEventEmitter: EventEmitter<AnalyticsGenericEvent>;

    @Event({
      eventName: 'search:dummyWidgetEvent',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      dummyBannerEventEmitter: EventEmitter<AnalyticsGenericEvent>;

    private getTabName() {
        if (!this.tabName) {
          this.tabName = this.el?.closest('uiaas-search-tab')?.['tabName'];
        }
    }

    private async getOffsiteBannersManagerData(reqData) {
        this._ajaxIsLoading = true;
        const resData = await fetchOffsiteBannersManagerData(reqData);
        this.bannerTimeout = setTimeout(() => {
            this._ajaxIsLoading = false;
        }, 500);
        return resData;
    }

    render() {
        this.getTabName();

        const data = this.bannerData as IWidgetData;
        const backgroundImagePath = data?.imageUrl;
        const isBannerVisible = data && data?.tabs?.includes(this.tabName);

        if (this.ajaxIsLoading) {
          return <OffsiteDummyBannerSkeleton />;
        }

        return ( isBannerVisible ? (
          <OffsitesWidgetV4EventsLayer
            eventEmitter={this.statisticsEventEmitter}
            tabName={this.analyticsTabName ?? this.tabName}
            widgetName={data.widgetName}
            searchTerm={this.term}
          >
            <FullWidthWidget
              data={data}
              tabName={this.tabName}
              isDashboard={this.isDashboard}
              isRtl={this.isRtl}
              computedPlacement={this.computedPlacement}
              backgroundImagePath={backgroundImagePath}
            />
          </OffsitesWidgetV4EventsLayer>
        ) : (
          this.isDashboard && this.brand === 'se' && <OffsitesDummyEventsLayer
            eventEmitter={this.dummyBannerEventEmitter}
            tabName={this.analyticsTabName ?? this.tabName}
            widgetName={'Dummy banner'}
            brand={this.brand}
            country={this.country}
            language={this.language}
            term={this.term}
          >
            <OffsitesDummyBanner
              brand={this.brand}
              country={this.country}
              language={this.language}
              tabName={this.tabName}
              isRtl={this.isRtl}
              isDashboard={this.isDashboard}
              computedPlacement={this.computedPlacement}
              labelProvider={this.labelProvider}
            />
          </OffsitesDummyEventsLayer>
        )

        );
    }
}
